/**
 * EntityModeContent
 *
 * Entity-mode body of the composite field sidebar, rendered by
 * `CompositeFieldSidebarContent` when the sidebar was opened against an entity
 * rather than a composite field type. It subscribes with
 * `useSnapshot(cellProxy, { sync: true })` to the same Valtio cell proxy
 * `ArCell` renders from, and only handles manual-entry values: the name input
 * and the composite fields are two writes into one `ArManualEntryValue`.
 *
 * "Switch to Reference" heads the body rather than closing it (SYD-2376): it is
 * the mode control for everything below, and it renders only when the opener
 * supplied `onSwitchToReference` — ReferenceSelect's own `toggleMode`. Its
 * accessible name repeats the visible label so voice control can reach it, and
 * it stays `danger`, matching the row context menu's entry for the same erase.
 *
 * Invariant: an entity with no `defaultUILayout` still gets a body. The layout
 * section is all that a missing structure removes, because this sidebar is the
 * only way out of manual mode for such a cell and returning null would strand it
 * there.
 */

import { FC } from 'react';
import { Button, Input, Popconfirm, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import type { ArCellValue, ArManualEntryValue } from '#types';
import type { CompositeFieldValue } from '#types/System.types';
import Icon from '../../atoms/Icon';
import { CompositeFieldProvider } from '../CompositeFieldProvider';
import LayoutUI from '../../rendering/LayoutUI';
import {
  closeCompositeFieldSidebar,
  getCellValueProxy,
  getOnChange,
  getEntityStructure,
  getOnSwitchToReference,
} from '../compositeFieldSidebarState';
import FlexDivider from '../../atoms/FlexDivider';
import FlexCol from '../../atoms/FlexCol';

type EntityModeContentProps = {
  entityId: string;
  cellProxy: NonNullable<ReturnType<typeof getCellValueProxy>>;
};

/** Entity mode: subscribes to the parent reference cell's Valtio proxy. */
export const EntityModeContent: FC<EntityModeContentProps> = ({ entityId, cellProxy }) => {
  const structure = getEntityStructure();
  const onChange = getOnChange();
  const onSwitchToReference = getOnSwitchToReference();
  const cellSnap = useSnapshot(cellProxy, { sync: true });
  const cellValue = cellSnap.value;
  const isManual =
    typeof cellValue === 'object' &&
    cellValue !== null &&
    'valueType' in cellValue &&
    cellValue.valueType === 'manual';
  const manualValue = isManual ? (cellValue as ArManualEntryValue) : undefined;
  const manualEntryName = manualValue?.value ?? '';
  const compositeValue: CompositeFieldValue | undefined = manualValue?.fields
    ? { fields: manualValue.fields, entityReferences: manualValue.entityReferences }
    : undefined;

  const handleChange = (raw: ArCellValue) => {
    if (!onChange) return;
    const next = raw as CompositeFieldValue;
    onChange({
      valueType: 'manual',
      value: manualEntryName || undefined,
      fields: next.fields,
      entityReferences: next.entityReferences,
    } satisfies ArManualEntryValue);
  };

  const handleNameChange = (nextName: string) => {
    onChange?.({
      valueType: 'manual',
      value: nextName || undefined,
      fields: manualValue?.fields,
      entityReferences: manualValue?.entityReferences,
    } satisfies ArManualEntryValue);
  };

  const handleSwitchToReference = () => {
    onSwitchToReference?.();
    closeCompositeFieldSidebar();
  };

  return (
    <FlexCol noGap>
      {onSwitchToReference && (
        <>
          <Popconfirm
            title="Switch to record reference?"
            description="Will erase this cell value."
            okText="Switch"
            onConfirm={handleSwitchToReference}
          >
            <Button
              type="text"
              aria-label="Switch to Reference"
              icon={<Icon.Search />}
              danger
              style={{ width: '100%', justifyContent: 'flex-start' }}
            >
              Switch to Reference
            </Button>
          </Popconfirm>
          <FlexDivider spacing={10} />
        </>
      )}
      <Typography.Text>
        Name
      </Typography.Text>
      <Input
        value={manualEntryName}
        onChange={(e) => handleNameChange(e.target.value)}
        placeholder="Record name"
        aria-label="manual-entry-name"
        style={{ marginBottom: 16 }}
      />
      {structure && (
        <>
          <FlexDivider spacing={10} />
          <CompositeFieldProvider
            entityId={entityId}
            value={compositeValue}
            onChange={handleChange}
          >
            <LayoutUI uiElement={structure} />
          </CompositeFieldProvider>
        </>
      )}
    </FlexCol>
  );
};
